import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import Footer from '../components/Footer';
import LandingNavbar from '../components/Navigation/LandingNavbar';
import RangeSlider from '../components/Form/RangeSlider';
import { getPlanCost, getFullPlanCost } from '../utils/price';

import '../styles/home.css';

const STEPS = [
	{
		id: '01',
		title: 'Comments',
		description:
			"The scrum master takes control of the board. Team members add comments to surface key topics, talking points, and tasks to prioritize. Cards can be added anonymously.",
		image: 'https://raw.githubusercontent.com/arvindrk/my-retro/master/landing-page%20assets/comments.svg',
		alt: 'comments',
		connector: 'https://raw.githubusercontent.com/arvindrk/my-retro/master/landing-page%20assets/leftToRight.svg',
		connectorAlt: 'connect',
	},
	{
		id: '02',
		title: 'Grouping',
		description:
			'Team members collaborate by grouping cards into thematic buckets. Related ideas from different themes can be combined.',
		image: 'https://raw.githubusercontent.com/arvindrk/my-retro/master/landing-page%20assets/group.svg',
		alt: 'group',
		connector: 'https://raw.githubusercontent.com/arvindrk/my-retro/master/landing-page%20assets/rightToLeft.svg',
		connectorAlt: 'connect',
	},
	{
		id: '03',
		title: 'Voting',
		description:
			'Everyone gets a vote. Team members vote on which topics, action items, and issues to discuss. The scrum master can split votes equally or customize the distribution.',
		image: 'https://raw.githubusercontent.com/arvindrk/my-retro/master/landing-page%20assets/vote.svg',
		alt: 'vote',
		connector: 'https://raw.githubusercontent.com/arvindrk/my-retro/master/landing-page%20assets/leftToRight.svg',
		connectorAlt: 'connect',
	},
	{
		id: '04',
		title: 'Discuss',
		description:
			'The team discusses topics in order of votes. Everyone contributes opinions, feedback, and solutions. As follow-up tasks come up, create action items on the fly and assign them to team members.',
		image: 'https://raw.githubusercontent.com/arvindrk/my-retro/master/landing-page%20assets/Discuss.svg',
		alt: 'discuss',
		connector: null,
	},
];

const FEATURES = [
	{
		title: 'Asynchronous Retrospective',
		description:
			"Team members contribute when it works for them. Communication flows anytime, anywhere. Extend the conversation beyond one meeting and keep the dialogue going.",
		image: 'https://raw.githubusercontent.com/arvindrk/my-retro/master/landing-page%20assets/async.svg',
		alt: 'async',
	},
	{
		title: 'Guided Facilitation',
		description:
			'Stay on topic with clear structure. Themes or topics keep each board focused. Your team stays engaged on what matters and the discussion stays productive.',
		image: 'https://raw.githubusercontent.com/arvindrk/my-retro/master/landing-page%20assets/design.svg',
		alt: 'guided',
	},
	{
		title: 'Appreciations',
		description:
			'Recognize people who go above and beyond. Appreciation columns capture highlights and achievements from each sprint. A simple way to boost morale and encourage collaboration.',
		image: 'https://raw.githubusercontent.com/arvindrk/my-retro/master/landing-page%20assets/appreciations.svg',
		alt: 'appreciations',
	},
	{
		title: 'Extensive Customization',
		description:
			'Every team is unique. Retro offers anonymity during comments, card author display, voting limits, and more. Teams control how their board works.',
		image: 'https://raw.githubusercontent.com/arvindrk/my-retro/master/landing-page%20assets/settings.svg',
		alt: 'settings',
	},
	{
		title: 'Action Items',
		description:
			'Pull action items from all your boards and teams into one place. Simplify tracking when you\'re on multiple projects.',
		image: 'https://raw.githubusercontent.com/arvindrk/my-retro/master/landing-page%20assets/collaborate.svg',
		alt: 'action items',
	},
];

const ALL_PLAN_FEATURES = [
	'Unlimited Retro Boards',
	'Dot Voting',
	'Action Plans',
	'Idea Grouping',
	'Guided Facilitation',
	'Unlimited Team Members',
	'Real-time Collaboration',
	'Team Appreciations',
];

const SINGLE_EXCLUDED = new Set([
	'Unlimited Team Members',
	'Real-time Collaboration',
	'Team Appreciations',
]);

const Home = () => {
	const [noOfTeams, setNoOfTeams] = useState(1);
	const [isAnnual, setIsAnnual] = useState(false);

	return (
		<>
			<LandingNavbar />
			<div className="main__content" id="landing__content">

				{/* Hero */}
				<section className="landing__section" id="home">
					<div className="section__content">
						<h1>Async retrospectives for agile teams</h1>
						<h2>Guided facilitation that keeps your retrospectives focused and productive.</h2>
						<Link to="/signup" className="hero-cta">Try for Free</Link>
						<div className="hero-image-wrapper">
							<img src="https://raw.githubusercontent.com/arvindrk/my-retro/master/landing.png" alt="landing" />
						</div>
					</div>
				</section>

				{/* How It Works */}
				<section className="landing__section" id="howItWorks">
					<div className="section__content">
						<h2 className="section__title">A 4-step process that works</h2>
					</div>
					{STEPS.map((step, index) => (
						<React.Fragment key={step.id}>
							<div className={`feature__row${index % 2 === 0 ? ' odd-row' : ''}`}>
								<div className="step__description">
									<span className="step-badge">{step.id}</span>
									<h3>{step.title}</h3>
									<p>{step.description}</p>
								</div>
								<div className="step__image">
									<img src={step.image} alt={step.alt} />
								</div>
							</div>
							{step.connector && (
								<div className="feature__row connect">
									<img src={step.connector} alt={step.connectorAlt} />
								</div>
							)}
						</React.Fragment>
					))}
				</section>

				{/* Features */}
				<section className="landing__section" id="features">
					<div className="section__content">
						<h2 className="section__title">Features</h2>
					</div>
					{FEATURES.map((feature, index) => (
						<div
							key={feature.title}
							className={`feature__row feature__row--feature${index % 2 === 0 ? ' odd-row' : ''}${index % 2 !== 0 ? ' feature__row--alt' : ''}`}
						>
							<div className="feature__description">
								<h3>{feature.title}</h3>
								<p>{feature.description}</p>
							</div>
							<div className="feature__image">
								<img src={feature.image} alt={feature.alt} />
							</div>
						</div>
					))}
				</section>

				{/* Pricing */}
				<section className="landing__section" id="pricing">
					<div className="lp__pricing-wrapper">

						<div className="lp__pricing-header">
							<h2 className="section__title">Simple pricing. Start for free.</h2>
							<p className="lp__pricing-subtitle">No credit card required.</p>
							<div className="lp__billing-toggle">
								<button
									className={`lp__billing-opt${!isAnnual ? ' lp__billing-opt--active' : ''}`}
									onClick={() => setIsAnnual(false)}
								>
									Monthly
								</button>
								<button
									className={`lp__billing-opt${isAnnual ? ' lp__billing-opt--active' : ''}`}
									onClick={() => setIsAnnual(true)}
								>
									Annual
									<span className="lp__save-tag">Save 17%</span>
								</button>
							</div>
						</div>

						<div className="lp__cards-grid">

							{/* Free Trial */}
							<div className="lp__plan-card lp__plan-card--featured">
								<div>
									<p className="lp__plan-name">
										Free Trial <span className="lp__plan-tag">14 days</span>
									</p>
									<div className="lp__plan-price">
										<span className="lp__plan-price--amount lp__plan-price--featured">$0</span>
										<span className="lp__plan-price--term">/ 14 days</span>
									</div>
								</div>
								<ul className="lp__plan-features">
									{ALL_PLAN_FEATURES.map(f => (
										<li key={f}><FaCheckCircle /> {f}</li>
									))}
								</ul>
							<div className="lp__plan-spacer" />
							<p className="lp__plan-cta-note">No credit card required</p>
							<Link to="/signup" className="lp__plan-cta lp__plan-cta--primary">
								Try free for 14 days →
							</Link>
							</div>

							{/* Single User */}
							<div className="lp__plan-card">
								<div>
									<p className="lp__plan-name">Single User</p>
									<div className="lp__plan-price">
										<span className="lp__plan-price--original" style={{ visibility: isAnnual ? 'visible' : 'hidden' }}>$60</span>
										<span className="lp__plan-price--amount">{isAnnual ? '$50' : '$5'}</span>
										<span className="lp__plan-price--term">/{isAnnual ? ' year' : ' month'}</span>
									</div>
								</div>
								<ul className="lp__plan-features">
									{ALL_PLAN_FEATURES.map(f => {
										const excluded = SINGLE_EXCLUDED.has(f);
										return (
											<li key={f} className={excluded ? 'excluded' : ''}>
												{excluded ? <FaTimesCircle /> : <FaCheckCircle />} {f}
											</li>
										);
									})}
								</ul>
								<div className="lp__plan-spacer" />
								<a href="/upgrade/single" className="lp__plan-cta">Get Solo</a>
							</div>

						{/* Team Plan */}
						<div className="lp__plan-card lp__plan-card--team">
							<div>
								<p className="lp__plan-name">Team Plan</p>
								<div className="lp__plan-price">
								<span className="lp__plan-price--original" style={{ visibility: isAnnual || noOfTeams > 2 ? 'visible' : 'hidden' }}>
									{'$' + getFullPlanCost({ isAnnual, isTeam: true, noOfTeams })}
								</span>
									<span className="lp__plan-price--amount">
										{'$' + getPlanCost({ isAnnual, isTeam: true, noOfTeams })}
									</span>
									<span className="lp__plan-price--term">/{isAnnual ? ' year' : ' month'}</span>
								</div>
							</div>
							<ul className="lp__plan-features">
								{ALL_PLAN_FEATURES.map(f => (
									<li key={f}><FaCheckCircle /> {f}</li>
								))}
							</ul>
							<div className="lp__team-slider-wrapper">
									<div className="lp__team-slider-header">
										<span className="lp__plan-label">Teams</span>
										<span className="lp__team-count">{noOfTeams} Team{noOfTeams > 1 ? 's' : ''}</span>
									</div>
									<RangeSlider
										start={1} end={10} step={1}
										value={noOfTeams}
										onChange={e => setNoOfTeams(e.target.value)}
									/>
									<div className="lp__tier-pills">
										<span className={`lp__tier-pill${noOfTeams <= 2 ? ' lp__tier-pill--active' : ''}`}>1–2 · $20</span>
										<span className={`lp__tier-pill${noOfTeams > 2 && noOfTeams <= 7 ? ' lp__tier-pill--active' : ''}`}>3–7 · $18</span>
										<span className={`lp__tier-pill${noOfTeams > 7 ? ' lp__tier-pill--active' : ''}`}>7+ · $16</span>
									</div>
						</div>
							<div className="lp__plan-spacer" />
							<a href="/upgrade/team" className="lp__plan-cta">Get Team</a>
							</div>

						</div>

					</div>
				</section>

				{/* Contact */}
				<section className="landing__section" id="contact">
					<div className="section__content">
						<div className="contact__card">
							<h2 className="section__title">Contact</h2>
							<p>Reach out to <a href="mailto:support@retrospective.pro">support@retrospective.pro</a> with any questions or suggestions.</p>
						</div>
					</div>
				</section>

				<Footer />
			</div>
		</>
	);
};

export default Home;
